import { notification } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useLazyGetUserAccessRightsQuery } from '@app/api/AccessRights';
import {
  useLazyGetUserByIdQuery,
  useLazyGetUserSettingsQuery,
} from '@app/api/User/api';
import { UserHistoryTab } from '@app/components';
import { UserIntegrationsTab } from '@app/components/UserIntegrationsTab/UserIntegrationsTab';
import { config } from '@app/config';
import { DESKTOP, FRONT_WARNING, SYSTEM_USER_ID } from '@app/constants';
import {
  useAccessRightsToAi,
  useNewUserAccessRights,
  useUserPrint,
  useWindowSize,
} from '@app/hooks';
import { EmployeeSettingsTab } from '@app/pages/Employees/EditEmployee/components';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { usePageContext } from '@app/providers/PageProvider/usePageContext';
import { handleCatchError } from '@app/services';
import { generateSpanWithDataTestId } from '@app/shared/utils';
import type { NEmployees, NUsers } from '@app/types';
import type { UserAccessRightsInterface } from '@app/types/NewAccessRight';
import { keysFirstLetterToLower, shortName } from '@app/utils';

import { AccessRightsTab } from '@pages/Users';
import {
  AiSettingsTab,
  GeneralInformationTab,
  getSalaryFrameHost,
  SALARY_SETTINGS_LABEL,
  SalarySettingsTab,
} from '@pages/Users/EditUser/components';
import { UserPreview } from '@pages/Users/UserPreview';

export const useEditUser = () => {
  const [activeKey, setActiveKey] = useState('1');
  const [user, setUser] = useState<NUsers.User>();
  const [searchParams] = useSearchParams();
  const [isLoadingPage, setIsLoadingPage] = useState(true);
  const [allSettings, setAllSettings] =
    useState<NEmployees.ModelUserSettings>();
  const [editableUserAccessRights, setEditableUserAccessRights] =
    useState<UserAccessRightsInterface | null>(null);
  const [editableUserUnsavedAccessRights, setEditableUserUnsavedAccessRights] =
    useState<UserAccessRightsInterface | null>(null);

  const { userId, database, activeBranchId } = useUserPrint();

  const navigate = useNavigate();

  const { showNotification } = useNotification();
  const [, contextHolder] = notification.useNotification();

  const { setTitleData } = usePageContext();

  const { innerWidth } = useWindowSize();

  const {
    userAccessRights: { AccessRights, Users, Salaries },
  } = useNewUserAccessRights();

  const hasUserRightToEdit = useMemo(() => {
    return Users.IsEdit;
  }, [Users]);
  const hasUserRightForModuleView = useMemo(() => {
    return AccessRights.IsOn;
  }, [AccessRights]);
  const { hasUserRightToEditAiOperator } = useAccessRightsToAi();

  const hasCurrentUserRightToViewAllRecords = useMemo(() => {
    return editableUserAccessRights?.Records.IsViewAllRecords;
  }, [editableUserAccessRights]);

  const [getUserAccessRights, { isLoading: isGetUserAccessRightsLoading }] =
    useLazyGetUserAccessRightsQuery();
  const [getUserById] = useLazyGetUserByIdQuery();
  const [getUserSettings] = useLazyGetUserSettingsQuery();

  const isAiOperator = useMemo(() => {
    return user?.isAi;
  }, [user]);

  const isDesktop = useMemo(() => {
    return innerWidth > DESKTOP;
  }, [innerWidth]);

  const isSystemUser = useMemo(() => {
    if (!user) {
      return true;
    }

    return user.id === SYSTEM_USER_ID;
  }, [user]);

  const isForbiddenPage = useMemo(() => {
    return isSystemUser || (isAiOperator && !hasUserRightToEditAiOperator);
  }, [isSystemUser, isAiOperator, hasUserRightToEditAiOperator]);

  const userName = useMemo(() => {
    if (!user) {
      return '';
    }

    const { nameFirst, nameLast } = user;

    return shortName(`${nameLast} ${nameFirst}`.trim(), !isDesktop, 23);
  }, [user, isDesktop]);

  const editableUserId = useMemo(
    () => Number(searchParams.get('id')),
    [searchParams]
  );

  const initialTab = useMemo(() => {
    return Number(searchParams.get('key'));
  }, [searchParams]);

  const initTab = useCallback(() => {
    const tab = initialTab || 1;
    setActiveKey(`${tab}`);
  }, [initialTab]);

  const handleTabClick = useCallback(
    (key: string) => {
      navigate(`/users/edit/?id=${editableUserId}&key=${key}`);
    },
    [editableUserId, navigate]
  );

  const shownEdit = useMemo(() => {
    return hasUserRightToEdit && !user?.isDeleted;
  }, [hasUserRightToEdit, user]);

  const shownAccessRights = useMemo(() => {
    return hasUserRightForModuleView && !user?.isDeleted;
  }, [hasUserRightForModuleView, user]);

  // Адрес сервиса задан только в .env.production: на dev и stage вкладки нет.
  const shownSalarySettings = useMemo(() => {
    return (
      Salaries.IsOn &&
      !user?.isDeleted &&
      Boolean(getSalaryFrameHost(config.salaryHost, editableUserId))
    );
  }, [Salaries, user, editableUserId]);

  const getDataHandler = useCallback(async () => {
    if (!userId || !database || !activeBranchId) return;
    setIsLoadingPage(true);
    try {
      const fetchedUser = await getUserById({
        getUserId: editableUserId,
        ActiveBranchId: activeBranchId,
      }).unwrap();
      const adaptedUser = keysFirstLetterToLower<NUsers.User>(fetchedUser);
      setUser(adaptedUser);
      setTitleData(
        `${adaptedUser?.nameLast || ''} ${adaptedUser.nameFirst}`.trim()
      );

      const settings = await getUserSettings({
        forUserId: editableUserId,
        ActiveBranchId: activeBranchId,
      }).unwrap();

      setAllSettings(settings);
    } catch (error) {
      showNotification(handleCatchError(error));
    } finally {
      setIsLoadingPage(false);
    }
  }, [
    userId,
    database,
    activeBranchId,
    editableUserId,
    showNotification,
    setTitleData,
    getUserById,
    getUserSettings,
  ]);

  const getUserAccessRightsHandler = useCallback(async () => {
    if (!editableUserId) {
      return showNotification(
        'Не удалось получить идентификатор пользователя',
        FRONT_WARNING
      );
    }

    if (!activeBranchId) {
      return;
    }

    try {
      const response = await getUserAccessRights({
        forUserId: editableUserId,
        ActiveBranchId: activeBranchId,
      }).unwrap();

      setEditableUserAccessRights(response);
      setEditableUserUnsavedAccessRights(response);
    } catch (error) {
      showNotification(error);
    }
  }, [editableUserId, activeBranchId, getUserAccessRights, showNotification]);

  const tabsItems = useMemo(() => {
    return [
      {
        label: generateSpanWithDataTestId(
          'Общая информация',
          'general-info-tab'
        ),
        key: '1',
        children: <UserPreview />,
      },
      ...(shownEdit
        ? [
            {
              label: generateSpanWithDataTestId('Редактировать', 'edit-tab'),
              key: '2',
              children: (
                <GeneralInformationTab
                  initUserData={user}
                  onUserUpdate={setUser}
                />
              ),
            },
          ]
        : []),
      ...(shownAccessRights
        ? [
            {
              label: generateSpanWithDataTestId('Права доступа', 'access-tab'),
              key: '3',
              children: (
                <AccessRightsTab
                  editableUserAccessRights={editableUserAccessRights}
                  editableUserUnsavedAccessRights={
                    editableUserUnsavedAccessRights
                  }
                  setEditableUserUnsavedAccessRights={
                    setEditableUserUnsavedAccessRights
                  }
                  isGetUserAccessRightsLoading={isGetUserAccessRightsLoading}
                  setEditableUserAccessRights={setEditableUserAccessRights}
                />
              ),
            },
          ]
        : []),
      ...(shownSalarySettings
        ? [
            {
              label: generateSpanWithDataTestId(
                SALARY_SETTINGS_LABEL,
                'salary-settings-tab'
              ),
              key: '8',
              children: <SalarySettingsTab userId={editableUserId} />,
            },
          ]
        : []),
      ...(hasCurrentUserRightToViewAllRecords
        ? [
            {
              label: generateSpanWithDataTestId(
                'Настройки уведомлений',
                'settings-tab'
              ),
              key: '4',
              children: (
                <EmployeeSettingsTab
                  employee={user}
                  allSettings={allSettings}
                  hasCurrentUserRightToViewAllRecords={
                    hasCurrentUserRightToViewAllRecords
                  }
                />
              ),
            },
          ]
        : []),
      {
        label: generateSpanWithDataTestId('История', 'history-tab'),
        key: '5',
        children: <UserHistoryTab user={user} />,
      },
      ...(!user?.isDeleted
        ? [
            {
              label: generateSpanWithDataTestId(
                'Социальные интеграции',
                'social-integration-tab'
              ),
              key: '6',
              children: <UserIntegrationsTab />,
            },
          ]
        : []),
      ...(isAiOperator
        ? [
            {
              label: generateSpanWithDataTestId('Настройки', 'ai-settings-tab'),
              key: '7',
              children: <AiSettingsTab />,
            },
          ]
        : []),
    ];
  }, [
    shownEdit,
    user,
    setUser,
    shownAccessRights,
    editableUserAccessRights,
    editableUserUnsavedAccessRights,
    isGetUserAccessRightsLoading,
    shownSalarySettings,
    editableUserId,
    hasCurrentUserRightToViewAllRecords,
    allSettings,
    isAiOperator,
  ]);

  useEffect(() => {
    return () => setTitleData('');
  }, [setTitleData]);

  return {
    contextHolder,
    isLoadingPage,
    activeKey,
    handleTabClick,
    tabsItems,
    isDesktop,
    isForbiddenPage,
    userName,
    getDataHandler,
    initTab,
    getUserAccessRightsHandler,
  };
};
